import { Link } from '@tanstack/react-router';
import { Show, SignInButton, UserButton } from '@clerk/tanstack-react-start';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppRole } from '../../hooks/useAppRole';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

type SiteNavbarProps = {
  variant?: 'overlay' | 'solid';
  className?: string;
};

export function SiteNavbar({ variant = 'solid', className }: SiteNavbarProps) {
  const { t, i18n } = useTranslation();
  const { clerkEnabled } = useAppRole();
  const overlay = variant === 'overlay';

  useEffect(() => {
    const lang = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'fr';
    document.documentElement.lang = lang;
  }, [i18n.language]);

  return (
    <header
      className={cn(
        'relative z-40 w-full',
        overlay
          ? 'absolute inset-x-0 top-0 bg-gradient-to-b from-white/50 to-transparent'
          : 'border-b border-primary-200/70 bg-white/80 backdrop-blur-md',
        className
      )}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:h-[4.5rem] sm:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img
            src="/brand/imaginette-logo.png"
            alt="Garderie Imaginette"
            className={cn('w-auto object-contain', overlay ? 'h-12 sm:h-14' : 'h-11 sm:h-12')}
          />
        </Link>

        <nav className="flex items-center gap-2 sm:gap-3">
          <div
            className="flex items-center rounded-full bg-white/70 p-0.5 text-xs font-bold uppercase tracking-wide shadow-sm shadow-primary-200/40"
            role="group"
            aria-label={t('siteNav.language')}
          >
            {(['fr', 'en'] as const).map((code) => {
              const active = i18n.language?.toLowerCase().startsWith(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => {
                    void i18n.changeLanguage(code);
                    document.documentElement.lang = code;
                  }}
                  aria-pressed={active}
                  className={cn(
                    'rounded-full px-2.5 py-1 transition',
                    active
                      ? 'bg-gradient-to-b from-primary-400 to-primary-500 text-white'
                      : 'text-primary-700/60 hover:text-primary-700'
                  )}
                >
                  {code === 'fr' ? 'FR' : 'EN'}
                </button>
              );
            })}
          </div>

          <Button
            asChild
            className="imaginette-btn-primary rounded-full px-5 font-semibold"
          >
            <Link to="/portal">{t('siteNav.portal')}</Link>
          </Button>

          {clerkEnabled ? (
            <>
              <Show when="signed-out">
                <SignInButton mode="modal" forceRedirectUrl="/portal">
                  <Button className="imaginette-btn-secondary rounded-full px-5 font-semibold">
                    {t('siteNav.signIn')}
                  </Button>
                </SignInButton>
              </Show>
              <Show when="signed-in">
                <UserButton />
              </Show>
            </>
          ) : null}
        </nav>
      </div>
    </header>
  );
}
